import { useEffect, useRef, useState, ReactNode } from "react";

interface LazyMountProps {
  children: ReactNode;
  /** Min height of placeholder while not mounted (avoids CLS) */
  minHeight?: number | string;
  /** rootMargin for IntersectionObserver — start mounting before in view */
  rootMargin?: string;
  /** Optional fallback while children are mounting */
  fallback?: ReactNode;
}

/**
 * Mounts children only when the placeholder enters (or approaches) the viewport.
 * Useful to defer heavy below-the-fold work (Leaflet map, third-party widgets, etc.)
 * and improve Lighthouse mobile scores (reduced TBT, faster LCP/FCP).
 */
const LazyMount = ({ children, minHeight = 400, rootMargin = "300px", fallback }: LazyMountProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (shouldRender) return;
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === "undefined") {
      setShouldRender(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShouldRender(true);
          io.disconnect();
        }
      },
      { rootMargin }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [shouldRender, rootMargin]);

  return (
    <div ref={ref} style={shouldRender ? undefined : { minHeight }}>
      {shouldRender ? children : fallback ?? null}
    </div>
  );
};

export default LazyMount;
